// Tracks which Y slots each column (keyed by X) already uses, so overflow cards never land on a real card.
export type OccupancyPoint = { x: number; y: number };

export type ColumnOccupancy = {
  claimFreeY: (colKey: string, candidateY: number, preferred: 1 | -1) => number;
};

export const createColumnOccupancy = (points: OccupancyPoint[], pitch: number): ColumnOccupancy => {
  const columns = new Map<string, number[]>();
  for (const point of points) {
    const column = columns.get(String(point.x));
    if (column) column.push(point.y);
    else columns.set(String(point.x), [point.y]);
  }
  for (const column of columns.values()) column.sort((a, b) => a - b);

  // Walk from candidateY in `direction` until the slot clears every occupant by a full card pitch.
  // 0.5px tolerance absorbs the fractional rows the position sweep can produce.
  const sweep = (column: number[], candidateY: number, direction: 1 | -1): number => {
    let y = candidateY;
    for (let i = 0; i < column.length; i++) {
      const occupiedY = column[direction === 1 ? i : column.length - 1 - i];
      if (Math.abs(occupiedY - y) < pitch - 0.5) y = occupiedY + direction * pitch;
    }
    return y;
  };

  const claimFreeY = (colKey: string, candidateY: number, preferred: 1 | -1): number => {
    const column = columns.get(colKey) ?? [];
    const preferredY = sweep(column, candidateY, preferred);
    const oppositeY = sweep(column, candidateY, preferred === 1 ? -1 : 1);
    // Both directions are collision-free; flipping off the preferred side has to save more than a full row to win.
    const flip = Math.abs(oppositeY - candidateY) < Math.abs(preferredY - candidateY) - pitch;
    const y = flip ? oppositeY : preferredY;

    const insertAt = column.findIndex((occupiedY) => occupiedY > y);
    column.splice(insertAt === -1 ? column.length : insertAt, 0, y);
    columns.set(colKey, column);
    return y;
  };

  return { claimFreeY };
};
